/**
 * CostAttributionApi — Observability Phase 2b cost attribution:
 * `/admin/costs/item/{id}`, `/by-source`, `/waste`, `/envelope`.
 *
 * Own mixin module (not folded into admin-platform-api.ts, which sits at the
 * file-size ratchet in scripts/ci/check_file_size_ratchet.py) — the same
 * pattern as quality-api.ts. Methods-only: prototype methods are merged onto
 * AdminAPIClient via applyMixins in client.ts. No instance fields here.
 */

import { APIError as SharedAPIError, buildApiUrl, createAuthHeaders, processApiResponse } from '../../../../shared/utils/api-helpers';
import { API_ENDPOINTS } from '@/utils/env';
import { transformKeysToCamelCase } from '@/utils/transform-keys';

import { BaseApiClient } from './base';

export type WasteClass = 'unclassified' | 'failed' | 'redelivered' | 'retried' | 'superseded' | 'used';
export const WASTE_CLASSES: WasteClass[] = ['failed', 'redelivered', 'retried', 'superseded', 'used', 'unclassified'];

export interface CostLedgerRow {
  id: string;
  createdAt: string | null;
  pipeline: string;
  stage: string;
  canonicalId: string;
  agentName: string;
  modelUsed: string;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  durationMs: number | null;
  celeryTaskId: string | null;
  celeryTaskName: string | null;
  taskExecutionId: string | null;
  processingRunId: string | null;
  wasteClass: WasteClass;
}

export interface CostItemLedgerResponse {
  contentId: string;
  title: string;
  contentType: string;
  platform: string;
  displayType: string;
  queueState: string | null;
  processingRunId: string | null;
  totalCostUsd: number;
  wastedCostUsd: number;
  classifiedShare: number;
  executions: number;
  rows: CostLedgerRow[];
}

export interface CostSourceGroup {
  groupId: string | null;
  name: string;
  platform?: string | null;
  organizationName?: string;
  items: number;
  calls: number;
  costUsd: number;
  costPerItemUsd: number | null;
  inputTokens: number;
  outputTokens: number;
}

export type CostGroupBy = 'source' | 'organization';

export interface CostsBySourceResponse {
  group: CostGroupBy;
  groups: CostSourceGroup[];
  totalCostUsd: number;
  periodDays: number;
  cached: boolean;
}

export interface WasteClassTotal {
  costUsd: number;
  calls: number;
}

export interface WasteTopItem {
  contentId: string;
  wastedCostUsd: number;
  classes: Partial<Record<WasteClass, number>>;
  title: string;
  contentType: string;
}

export interface CostsWasteResponse {
  totalCostUsd: number;
  wastedCostUsd: number;
  classifiedShare: number;
  totalsByClass: Record<WasteClass, WasteClassTotal>;
  // Keys are raw pipeline names (creator_pool, …) — never camelCased.
  // Restored post-transform in getCostsWaste (same rule as quality-api scores).
  byPipeline: Record<string, Partial<Record<WasteClass, number>>>;
  topItems: WasteTopItem[];
  periodDays: number;
  cached: boolean;
}

export interface EnvelopeRow {
  canonicalId: string;
  baselineP50: number;
  baselineP95: number;
  windowP50: number | null;
  windowP95: number | null;
  drift: number | null;
  calls: number;
}

export interface EnvelopeRawRow {
  canonicalId: string;
  windowP50: number | null;
  windowP95: number | null;
  calls: number;
}

export interface CostsEnvelopeResponse {
  withEnvelope: EnvelopeRow[];
  withoutEnvelope: EnvelopeRawRow[];
  periodDays: number;
  cached: boolean;
}

export class CostAttributionApi extends BaseApiClient {
  private async fetchJson<T>(path: string): Promise<T> {
    const url = buildApiUrl(this.baseUrl, path);
    const headers = createAuthHeaders(await this.resolveToken());
    const response = await fetch(url, { headers });
    const result = await processApiResponse<T>(response);
    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }
    return result.data as T;
  }

  async getCostItemLedger(contentId: string): Promise<CostItemLedgerResponse> {
    const raw = await this.fetchJson<unknown>(`${API_ENDPOINTS.adminCostsItem}/${encodeURIComponent(contentId)}`);
    return transformKeysToCamelCase<CostItemLedgerResponse>(raw);
  }

  async getCostsBySource(days: number = 30, group: CostGroupBy = 'source', limit: number = 50): Promise<CostsBySourceResponse> {
    const raw = await this.fetchJson<unknown>(`${API_ENDPOINTS.adminCostsBySource}?days=${days}&group=${group}&limit=${limit}`);
    return transformKeysToCamelCase<CostsBySourceResponse>(raw);
  }

  async getCostsWaste(days: number = 7): Promise<CostsWasteResponse> {
    const raw = await this.fetchJson<{ by_pipeline: CostsWasteResponse['byPipeline'] }>(`${API_ENDPOINTS.adminCostsWaste}?days=${days}`);
    const data = transformKeysToCamelCase<CostsWasteResponse>(raw);
    data.byPipeline = Object.fromEntries(
      Object.entries(raw.by_pipeline ?? {}).map(([pipeline, classes]) => [pipeline, transformKeysToCamelCase<Partial<Record<WasteClass, number>>>(classes)])
    );
    return data;
  }

  async getCostsEnvelope(days: number = 7): Promise<CostsEnvelopeResponse> {
    const raw = await this.fetchJson<unknown>(`${API_ENDPOINTS.adminCostsEnvelope}?days=${days}`);
    return transformKeysToCamelCase<CostsEnvelopeResponse>(raw);
  }
}
